import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Building2, Users, MapPin, BadgeCheck, Pin, PinOff } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareStartupDialog } from "./ShareStartupDialog";

interface PinnedStartup {
  id: string;
  name: string;
  short_description: string;
  category: string;
  team_size: number;
  stage: string;
  banner_url: string | null;
  location: string;
  profiles: {
    is_pro: boolean;
  };
  pinned_startups: {
    id: string;
  }[];
}

interface StartupGridProps {
  startups: PinnedStartup[];
  isAdmin: boolean;
  onPin: (startupId: string) => void;
  onUnpin: (startupId: string) => void;
  title?: string;
}

export function StartupGrid({ startups, isAdmin, onPin, onUnpin, title }: StartupGridProps) {
  const renderStartupCard = (startup: PinnedStartup) => {
    const isPinned = startup.pinned_startups?.length > 0;

    return (
      <div key={startup.id} className="relative">
        {isAdmin && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10"
            onClick={() => isPinned ? onUnpin(startup.id) : onPin(startup.id)}
          >
            {isPinned ? (
              <PinOff className="h-4 w-4 text-flux-cyan" />
            ) : (
              <Pin className="h-4 w-4" />
            )}
          </Button>
        )}
        <Link to={`/communities/startups/${startup.id}`}>
          <Card className="bg-flux-black/50 border-flux-cyan/20 hover:border-flux-cyan/40 transition-all duration-300 h-full">
            <CardHeader>
              {startup.banner_url ? (
                <img 
                  src={startup.banner_url} 
                  alt={startup.name}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-flux-cyan/20 to-flux-cyan/5 rounded-t-lg flex items-center justify-center">
                  <Building2 className="w-16 h-16 text-flux-cyan/40" />
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-xl text-flux-cyan">{startup.name}</CardTitle>
                    {startup.profiles?.is_pro && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <BadgeCheck className="h-6 w-6 text-yellow-500 drop-shadow-[0_0_5px_rgba(234,179,8,0.5)] animate-pulse filter brightness-125" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Founded by a Pro Member</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <ShareStartupDialog startup={startup} />
                </div>
                <CardDescription className="text-gray-400 line-clamp-3">
                  {startup.short_description}
                </CardDescription>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    <span>{startup.team_size} member{startup.team_size !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 rounded-full bg-flux-cyan/10 text-flux-cyan">
                      {startup.category}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-flux-purple/10 text-flux-purple">
                      {startup.stage}
                    </span>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-400">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{startup.location}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    );
  };

  return (
    <>
      {title && <h2 className="text-2xl font-bold mb-6 text-flux-cyan">{title}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {startups.map(renderStartupCard)}
      </div>
    </>
  );
}
